import Phaser from 'phaser'
import type { Player } from '../entities/Player'
import { sampleCards, CARDS, SYNERGIES, type CardDef } from '../config/cards'
import { audioManager } from '../audio/AudioManager'
import { t } from '../../i18n'
import { hapticImpactLight, hapticSuccess } from '../../telegram/webApp'

const LEVEL_UP_INTERVAL = 30_000 // ms — level up every 30 seconds
const CARDS_TO_SHOW = 3

const RARITY_COLORS: Record<CardDef['rarity'], number> = {
  common: 0x888888,
  rare: 0x3399ff,
  epic: 0xaa44ff,
  legendary: 0xffaa00,
}

const TYPE_COLORS: Record<CardDef['type'], string> = {
  weapon: '#ff6644',
  passive: '#44aaff',
  special: '#ffcc00',
}

/** Tracks which cards the player owns and how many stacks of each. */
type CardInventory = Record<string, number>

/** Synergies that have already been applied (to avoid double-applying). */
type TriggeredSynergies = Set<string>
type DisableableInteractiveObject = Phaser.GameObjects.GameObject & { disableInteractive: () => void }

/**
 * UpgradeSystem
 *
 * - Counts elapsed run time and fires a level-up every 30 seconds.
 * - Pauses the game and shows a blocking modal overlay with 3 card choices.
 * - Applies the chosen card to the player's stats (stacks on repeat picks).
 * - Checks and applies synergy bonuses after each card pick.
 * - Resumes the game once the player selects a card.
 */
export class UpgradeSystem {
  private scene: Phaser.Scene
  private player: Player

  private elapsed = 0
  private nextLevelUp = LEVEL_UP_INTERVAL
  private level = 0
  private cardInventory: CardInventory = {}
  private triggeredSynergies: TriggeredSynergies = new Set()

  /** True while the level-up modal is open. */
  isOpen = false

  private overlay: Phaser.GameObjects.Container | null = null
  private onResumeCallback: (() => void) | null = null
  private cardVisualGroups: Phaser.GameObjects.GameObject[][] = []

  constructor(scene: Phaser.Scene, player: Player) {
    this.scene = scene
    this.player = player
  }

  /**
   * Call every frame with delta in ms.
   * Returns true if a level-up modal just opened (caller should pause gameplay).
   */
  update(delta: number): boolean {
    if (this.isOpen) return false

    this.elapsed += delta
    if (this.elapsed >= this.nextLevelUp) {
      this.nextLevelUp += LEVEL_UP_INTERVAL
      this.showModal()
      return true
    }
    return false
  }

  /** Register a callback to be invoked when the player closes the modal. */
  onResume(cb: () => void) {
    this.onResumeCallback = cb
  }

  /** Card inventory for external inspection (e.g. HUD). */
  get inventory(): Readonly<CardInventory> {
    return this.cardInventory
  }

  get currentLevel() {
    return this.level
  }

  /** Apply instant random level rewards (used for meta progression at run start). */
  grantInstantLevels(levels: number): void {
    const safeLevels = Math.max(0, Math.floor(levels))
    for (let i = 0; i < safeLevels; i++) {
      this.level++
      const [card] = sampleCards(1, this.player.stats.luckBonus)
      if (card) this.applyCard(card)
    }
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  private showModal() {
    this.isOpen = true
    this.level++
    audioManager.play('levelUp')
    hapticSuccess()

    const cards = sampleCards(CARDS_TO_SHOW, this.player.stats.luckBonus)
    const { width, height } = this.scene.scale

    this.overlay = this.scene.add.container(0, 0).setDepth(100)
    this.cardVisualGroups = []

    // Dim background
    const bg = this.scene.add.graphics()
    bg.fillStyle(0x000000, 0.75)
    bg.fillRect(0, 0, width, height)
    this.overlay.add(bg)

    // Panel background
    const panelW = Math.min(width - 40, 480)
    const panelH = 340
    const panelX = (width - panelW) / 2
    const panelY = (height - panelH) / 2

    const panel = this.scene.add.graphics()
    panel.fillStyle(0x0d1b2a, 1)
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 16)
    panel.lineStyle(2, 0x00aaff, 0.8)
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 16)
    this.overlay.add(panel)

    // Title
    const title = this.scene.add
      .text(width / 2, panelY + 22, `⬆ ${t('game.level')} ${this.level} — ${t('upgrade.choose')}`, {
        fontSize: '18px',
        color: '#00d4ff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 0)
    this.overlay.add(title)

    // Card buttons
    const cardW = Math.floor((panelW - 40) / CARDS_TO_SHOW - 10)
    const cardH = 210
    const cardAreaTop = panelY + 62

    cards.forEach((card, i) => {
      const cx = panelX + 20 + i * (cardW + 10)
      this.buildCardButton(cx, cardAreaTop, cardW, cardH, card, i)
    })
  }

  private buildCardButton(
    x: number,
    y: number,
    w: number,
    h: number,
    card: CardDef,
    cardIndex: number,
  ) {
    if (!this.overlay) return

    const borderColor = RARITY_COLORS[card.rarity]
    const stacks = this.cardInventory[card.id] ?? 0

    // Card background
    const cardBg = this.scene.add.graphics()
    cardBg.fillStyle(0x16213e, 1)
    cardBg.fillRoundedRect(x, y, w, h, 10)
    cardBg.lineStyle(2, borderColor, 1)
    cardBg.strokeRoundedRect(x, y, w, h, 10)

    // Hover zone
    const zone = this.scene.add
      .zone(x, y, w, h)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })

    zone.on('pointerover', () => {
      cardBg.clear()
      cardBg.fillStyle(0x1e3a5f, 1)
      cardBg.fillRoundedRect(x, y, w, h, 10)
      cardBg.lineStyle(3, borderColor, 1)
      cardBg.strokeRoundedRect(x, y, w, h, 10)
    })

    zone.on('pointerout', () => {
      cardBg.clear()
      cardBg.fillStyle(0x16213e, 1)
      cardBg.fillRoundedRect(x, y, w, h, 10)
      cardBg.lineStyle(2, borderColor, 1)
      cardBg.strokeRoundedRect(x, y, w, h, 10)
    })

    zone.on('pointerdown', () => {
      hapticImpactLight()
      this.playCardSelectionAnimation(card, cardIndex)
    })

    // Type badge (small pill at top-left)
    const typeBadge = this.scene.add
      .text(x + 6, y + 6, t(`upgrade.type.${card.type}`), {
        fontSize: '9px',
        color: TYPE_COLORS[card.type],
        fontStyle: 'bold',
        backgroundColor: '#00000080',
        padding: { left: 3, right: 3, top: 1, bottom: 1 },
      })
      .setOrigin(0, 0)

    // Icon
    const icon = this.scene.add
      .text(x + w / 2, y + 36, card.icon, { fontSize: '40px' })
      .setOrigin(0.5)

    // Name
    const name = this.scene.add
      .text(x + w / 2, y + 88, t(`card.${card.id}.name`), {
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
        wordWrap: { width: w - 8 },
        align: 'center',
      })
      .setOrigin(0.5, 0)

    // Description
    const desc = this.scene.add
      .text(x + w / 2, y + 116, t(`card.${card.id}.description`), {
        fontSize: '11px',
        color: '#aaccee',
        wordWrap: { width: w - 10 },
        align: 'center',
      })
      .setOrigin(0.5, 0)

    // Stack counter
    const stackLabel = stacks > 0
      ? this.scene.add
          .text(x + w / 2, y + h - 28, `${t('upgrade.owned')}: ${stacks}×`, {
            fontSize: '11px',
            color: '#ffdd44',
            fontStyle: 'italic',
          })
          .setOrigin(0.5, 0)
      : null

    // Rarity label
    const rarityLabel = this.scene.add
      .text(x + w / 2, y + h - 14, t(`rarity.${card.rarity}`), {
        fontSize: '10px',
        color: `#${borderColor.toString(16).padStart(6, '0')}`,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1)

    this.overlay.add([cardBg, zone, typeBadge, icon, name, desc, rarityLabel])
    if (stackLabel) this.overlay.add(stackLabel)

    const visuals = [cardBg, typeBadge, icon, name, desc, rarityLabel]
    if (stackLabel) visuals.push(stackLabel)
    this.cardVisualGroups[cardIndex] = visuals
  }

  private playCardSelectionAnimation(card: CardDef, selectedIndex: number): void {
    if (!this.overlay) return

    for (const obj of this.overlay.list) {
      if (this.isInteractiveObject(obj)) {
        obj.disableInteractive()
      }
    }

    this.cardVisualGroups.forEach((group, index) => {
      this.scene.tweens.add({
        targets: group,
        alpha: index === selectedIndex ? 1 : 0.2,
        scaleX: index === selectedIndex ? 1.08 : 1,
        scaleY: index === selectedIndex ? 1.08 : 1,
        duration: 180,
        ease: 'Power2',
      })
    })

    this.scene.time.delayedCall(220, () => {
      this.applyCard(card)
      this.closeModal()
    })
  }

  private applyCard(card: CardDef) {
    const level = this.cardInventory[card.id] ?? 0
    card.apply(this.player.stats, level)
    this.cardInventory[card.id] = level + 1
    this.checkSynergies()
  }

  /**
   * Check each synergy definition against the current inventory.
   * If the stack threshold is met and the synergy hasn't fired yet, apply it.
   * Supports both card-stack synergies (same card picked N times) and
   * element synergies (N distinct cards sharing the same element).
   */
  private checkSynergies() {
    // Count distinct owned cards per element for element-based synergies
    const elementCounts: Record<string, number> = {}
    for (const [cardId, stacks] of Object.entries(this.cardInventory)) {
      if (stacks > 0) {
        const card = CARDS.find((c) => c.id === cardId)
        if (card?.element) {
          elementCounts[card.element] = (elementCounts[card.element] ?? 0) + 1
        }
      }
    }

    for (const synergy of SYNERGIES) {
      const key = synergy.element
        ? `element:${synergy.element}:${synergy.threshold}`
        : `card:${synergy.cardId}:${synergy.threshold}`
      if (this.triggeredSynergies.has(key)) continue

      const count = synergy.element
        ? (elementCounts[synergy.element] ?? 0)
        : (this.cardInventory[synergy.cardId!] ?? 0)

      if (count >= synergy.threshold) {
        synergy.apply(this.player.stats)
        this.triggeredSynergies.add(key)
        this.showSynergyBanner(synergy.label)
      }
    }
  }

  /** Display a brief synergy-unlocked banner. */
  private showSynergyBanner(label: string) {
    const { width, height } = this.scene.scale
    const banner = this.scene.add
      .text(width / 2, height * 0.15, `${t('upgrade.synergy_unlocked')}\n${label}`, {
        fontSize: '20px',
        color: '#ffdd44',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(200)

    this.scene.tweens.add({
      targets: banner,
      alpha: 0,
      y: height * 0.08,
      duration: 2500,
      ease: 'Power2',
      onComplete: () => banner.destroy(),
    })
  }

  private closeModal() {
    this.overlay?.destroy()
    this.overlay = null
    this.isOpen = false
    this.onResumeCallback?.()
  }

  /** Close the upgrade modal from an external trigger (e.g. Telegram back button). */
  closeModalFromBack(): void {
    if (!this.isOpen) return
    this.closeModal()
  }

  private isInteractiveObject(obj: Phaser.GameObjects.GameObject): obj is DisableableInteractiveObject {
    return typeof (obj as Partial<DisableableInteractiveObject>).disableInteractive === 'function'
  }
}
